import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Play, Mail, Check, ChevronLeft, ChevronRight, Smartphone } from 'lucide-react';
import { useNespresso } from '../context/NespressoContext';

// Nespresso brand colors
const NESPRESSO = {
  brown: '#986f38',
  brownHover: '#815e2f',
  gold: '#c69f36',
  black: '#000000',
  darkGray: '#292929',
  lightGray: '#f9f9f9',
};

// Video configurations
const CELEBRATION_VIDEOS = [
  {
    id: 'base',
    title: 'First Steps',
    description: 'Celebrate starting your recycling journey',
    pods: '1-99 pods',
    src: '/assets/videos/celebration-base.mp4',
    thumbnail: null,
  },
  {
    id: '100',
    title: 'Planet Hero',
    description: 'Fireworks celebration for reaching 100 pods',
    pods: '100+ pods',
    src: '/assets/videos/celebration-100.mp4',
    thumbnail: null,
  },
  {
    id: '500',
    title: 'Eco Champion',
    description: 'Magical waterfall of pods transforming to butterflies',
    pods: '500+ pods',
    src: '/assets/videos/celebration-500.mp4',
    thumbnail: null,
  },
  {
    id: '1000-horizontal',
    title: 'Legendary Status',
    description: 'Cosmic galaxy of recycled pods',
    pods: '1000+ pods',
    src: '/assets/videos/celebration-1000-horizontal.mp4',
    thumbnail: null,
  },
  {
    id: '1000-vertical',
    title: 'Legendary (Stories)',
    description: 'Vertical format for Instagram/TikTok Stories',
    pods: '1000+ pods (9:16)',
    src: '/assets/videos/celebration-1000-vertical.mp4',
    thumbnail: null,
  },
];

interface VideoShareViewProps {
  onClose?: () => void;
}

const VideoShareView: React.FC<VideoShareViewProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { state } = useNespresso();
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState(state.userEmail || '');
  const [emailSent, setEmailSent] = useState(false);

  const selectedVideo = CELEBRATION_VIDEOS[selectedVideoIndex];

  // Generate QR code URL for the video
  const getQRCodeUrl = (videoSrc: string) => {
    // In production, this would be a full URL to the hosted video
    const fullUrl = `${window.location.origin}${videoSrc}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(fullUrl)}&color=1A1A1A&bgcolor=FFFFFF`;
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/nespresso/offer');
    }
  };

  const handlePrevVideo = () => {
    setSelectedVideoIndex((prev) =>
      prev === 0 ? CELEBRATION_VIDEOS.length - 1 : prev - 1
    );
    setIsPlaying(false);
  };

  const handleNextVideo = () => {
    setSelectedVideoIndex((prev) =>
      prev === CELEBRATION_VIDEOS.length - 1 ? 0 : prev + 1
    );
    setIsPlaying(false);
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate email sending
    await new Promise(resolve => setTimeout(resolve, 1000));
    setEmailSent(true);
    setTimeout(() => {
      setShowEmailForm(false);
      setEmailSent(false);
    }, 2000);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-between">
        <img
          src="/assets/images/logos/nespresso-white.png"
          alt="Nespresso"
          className="h-6 w-auto"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </header>

      {/* Title */}
      <div className="text-center px-8 mb-6">
        <p
          className="text-sm uppercase tracking-widest font-light mb-2"
          style={{ color: NESPRESSO.gold }}
        >
          Share Your Achievement
        </p>
        <h1 className="text-white text-2xl font-light">
          Choose Your Celebration Video
        </h1>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 px-8 py-4">
        {/* Video Player Section */}
        <div className="flex flex-col items-center">
          {/* Video Navigation */}
          <div className="flex items-center gap-4 mb-4">
            <motion.button
              onClick={handlePrevVideo}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </motion.button>

            <div className="text-center min-w-[200px]">
              <h2 className="text-white text-lg font-medium">{selectedVideo.title}</h2>
              <p className="text-white/50 text-sm">{selectedVideo.pods}</p>
            </div>

            <motion.button
              onClick={handleNextVideo}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </motion.button>
          </div>

          {/* Video Preview */}
          <motion.div
            className="relative rounded-2xl overflow-hidden shadow-2xl"
            style={{
              width: selectedVideo.id.includes('vertical') ? 270 : 480,
              maxWidth: '90vw',
            }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            key={selectedVideo.id}
          >
            <video
              src={selectedVideo.src}
              className="w-full h-auto"
              controls={isPlaying}
              autoPlay={isPlaying}
              loop
              playsInline
              onClick={() => setIsPlaying(true)}
            />

            {/* Play overlay */}
            {!isPlaying && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer"
                onClick={() => setIsPlaying(true)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <motion.div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: NESPRESSO.gold }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Play className="w-8 h-8 text-black ml-1" fill="black" />
                </motion.div>
              </motion.div>
            )}
          </motion.div>

          {/* Video Description */}
          <p className="text-white/60 text-sm mt-4 text-center max-w-[400px]">
            {selectedVideo.description}
          </p>

          {/* Video Dots */}
          <div className="flex gap-2 mt-4">
            {CELEBRATION_VIDEOS.map((video, index) => (
              <button
                key={video.id}
                onClick={() => {
                  setSelectedVideoIndex(index);
                  setIsPlaying(false);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === selectedVideoIndex
                    ? 'w-6'
                    : 'bg-white/30 hover:bg-white/50'
                }`}
                style={{
                  backgroundColor: index === selectedVideoIndex ? NESPRESSO.gold : undefined,
                }}
              />
            ))}
          </div>
        </div>

        {/* QR & Share Section */}
        <div className="flex flex-col items-center gap-6">
          {/* QR Code */}
          <motion.div
            className="flex flex-col items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div
              className="relative bg-white p-4 rounded-2xl"
              style={{
                boxShadow: `0 20px 60px rgba(0, 0, 0, 0.4), 0 0 40px ${NESPRESSO.gold}20`,
              }}
            >
              <img
                src={getQRCodeUrl(selectedVideo.src)}
                alt="QR code to download video"
                width={180}
                height={180}
                className="block"
              />
            </div>

            <div className="flex items-center gap-2 text-white/70 mt-4">
              <Smartphone size={18} strokeWidth={1.5} />
              <span className="text-sm font-light">Scan to get on your phone</span>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="flex items-center gap-4 w-full max-w-[250px]">
            <div className="flex-1 h-px bg-white/20" />
            <span className="text-white/40 text-sm">or</span>
            <div className="flex-1 h-px bg-white/20" />
          </div>

          {/* Email Option */}
          <AnimatePresence mode="wait">
            {!showEmailForm ? (
              <motion.button
                key="email-button"
                onClick={() => setShowEmailForm(true)}
                className="flex items-center gap-3 px-6 py-3 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: NESPRESSO.brown }}
                whileHover={{ scale: 1.02, backgroundColor: NESPRESSO.brownHover }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Mail size={20} />
                <span>Send to Email</span>
              </motion.button>
            ) : (
              <motion.form
                key="email-form"
                onSubmit={handleSendEmail}
                className="flex flex-col gap-3 w-full max-w-[280px]"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEmailForm(false)}
                    className="flex-1 px-4 py-3 rounded-lg bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    type="submit"
                    disabled={emailSent}
                    className="flex-1 px-4 py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors"
                    style={{ backgroundColor: emailSent ? '#22c55e' : NESPRESSO.brown }}
                    whileHover={!emailSent ? { backgroundColor: NESPRESSO.brownHover } : {}}
                  >
                    {emailSent ? (
                      <>
                        <Check size={18} />
                        Sent!
                      </>
                    ) : (
                      'Send'
                    )}
                  </motion.button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Social sharing hint */}
          <p className="text-white/40 text-xs text-center max-w-[250px]">
            Download the video to share on TikTok, Instagram Reels, or any social platform
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10 font-light">
        <p>Share your achievement and inspire others to recycle</p>
      </footer>
    </div>
  );
};

export default VideoShareView;
